import {
    Button,
    Classes,
    Icon,
    InputGroup,
    Intent,
    Tag
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    ColDef,
    ICellEditor,
    ICellEditorParams,
    ICellRendererParams
} from "ag-grid-community";
import classNames from "classnames";
import { getCostAssumptionText } from "in_queue/utils/scenarios";
import { isEqual } from "lodash";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import { TextLinkButton } from "../common/TextLinkButton";
import { ProjectSizeAssumptionsMetadata } from "../ScenarioBuilder/scenarioBuilder.helpers";
import { ScenarioBuilderLabel } from "../ScenarioBuilder/scenarioBuilderParams";
import { SensitivitiesSummary } from "../tables/SensitivitiesSummary";
import { BulkScenarioDraft } from "./bulkScenarioDrafts";
import css from "./BulkScenarioTable.module.scss";
import { BulkScenarioUploadLabel } from "./bulkScenarioUploadLabels";

export interface BulkScenarioRow {
    readonly draft: BulkScenarioDraft;
    readonly sizeMetadata: ProjectSizeAssumptionsMetadata;
    readonly exceedsMwThreshold: boolean;
    readonly warningCount: number;
    readonly customUpgradeName: string | undefined;
}

const TITLE_COLUMN_ID = "title";

export const DEFAULT_COL_DEF: ColDef<BulkScenarioRow> = {
    resizable: true,
    unSortIcon: true,
    cellStyle: { display: "flex", alignItems: "center" }
};

const warningsTagLabel = (count: number): string =>
    `${count} ${count === 1 ? "warning" : "warnings"}, show details`;

// A flex cell drops ag-grid's own ellipsis on bare text, so the text needs
// an element of its own to truncate.
const TruncatedTextCell: React.FC<
    ICellRendererParams<BulkScenarioRow, string>
> = ({ value }) => (
    <span className={css["BulkScenarioTable-text"]}>{value}</span>
);

const TitleCell: React.FC<ICellRendererParams<BulkScenarioRow, string>> = ({
    value,
    node,
    api
}) => {
    if (value == null) {
        return null;
    }
    return (
        <span className={css["BulkScenarioTable-title"]}>
            <span className={css["BulkScenarioTable-text"]} title={value}>
                {value}
            </span>
            <Button
                className={css["BulkScenarioTable-rename"]}
                variant="minimal"
                size="small"
                icon={IconNames.EDIT}
                aria-label={`Rename ${value}`}
                onClick={() => {
                    if (node.rowIndex !== null) {
                        api.startEditingCell({
                            rowIndex: node.rowIndex,
                            colKey: TITLE_COLUMN_ID
                        });
                    }
                }}
            />
        </span>
    );
};

// Commits on blur itself: the grid's own lose-focus handling relies on cell
// focus, which `suppressCellFocus` turns off.
const TitleEditor = forwardRef<
    ICellEditor<string>,
    ICellEditorParams<BulkScenarioRow, string>
>(function TitleEditor({ value, api, eGridCell }, ref) {
    const [title, setTitle] = useState(value ?? "");
    const [cellRect] = useState(() => eGridCell.getBoundingClientRect());
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            getValue: () => title.trim(),
            isCancelAfterEnd: () => title.trim() === ""
        }),
        [title]
    );

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    return (
        <div
            className={css["BulkScenarioTable-title-editor"]}
            style={{ minWidth: cellRect.width, height: cellRect.height }}
        >
            {/* Shares the input's font and padding, so the editor grows to fit the title. */}
            <span
                aria-hidden
                className={classNames(
                    Classes.INPUT,
                    css["BulkScenarioTable-title-editor-sizer"]
                )}
            >
                {title}
            </span>
            <InputGroup
                className={css["BulkScenarioTable-title-editor-input"]}
                inputRef={inputRef}
                value={title}
                aria-label="Scenario title"
                onValueChange={setTitle}
                onBlur={() => api.stopEditing()}
            />
        </div>
    );
});

const WarningsCell: React.FC<{
    readonly count: number;
    readonly onShow: () => void;
}> = ({ count, onShow }) =>
    count > 0 ? (
        <Tag
            minimal
            round
            interactive
            role="button"
            intent={Intent.WARNING}
            icon={IconNames.WARNING_SIGN}
            aria-label={warningsTagLabel(count)}
            onClick={onShow}
        >
            {count}
        </Tag>
    ) : (
        <span className={Classes.TEXT_MUTED}>None</span>
    );

const isAdjusted = (metadata: ProjectSizeAssumptionsMetadata): boolean =>
    metadata.droppedProjectCount + metadata.downsizedProjectCount > 0 &&
    metadata.clusterMw > 0;

interface SensitivitiesValue {
    readonly scenarioTitle: string;
    readonly sizeMetadata: ProjectSizeAssumptionsMetadata;
    readonly exceedsMwThreshold: boolean;
}

const SensitivitiesCell: React.FC<{
    readonly value: SensitivitiesValue;
    readonly onOpen: () => void;
}> = ({
    value: { scenarioTitle, sizeMetadata, exceedsMwThreshold },
    onOpen
}) => {
    if (!isAdjusted(sizeMetadata)) {
        return (
            <TextLinkButton
                text={ScenarioBuilderLabel.Sensitivities}
                ariaLabel={`${ScenarioBuilderLabel.Sensitivities} for ${scenarioTitle}`}
                onClick={onOpen}
            />
        );
    }
    return (
        <span className={css["BulkScenarioTable-sensitivities"]}>
            <SensitivitiesSummary
                mwRemoved={sizeMetadata.mwRemoved}
                clusterMw={sizeMetadata.clusterMw}
                link={{
                    onClick: onOpen,
                    ariaLabel: `Edit sensitivities for ${scenarioTitle}`
                }}
            />
            {exceedsMwThreshold && (
                <Icon icon={IconNames.WARNING_SIGN} intent={Intent.WARNING} />
            )}
        </span>
    );
};

const titleColumn = (
    onRename: (id: string, title: string) => void
): ColDef<BulkScenarioRow, string> => ({
    colId: TITLE_COLUMN_ID,
    headerName: "Scenario",
    flex: 2,
    minWidth: 200,
    editable: true,
    valueGetter: ({ data }) => data?.draft.title,
    // The page owns the drafts, so the grid leaves its row data unchanged.
    valueSetter: ({ data, newValue }) => {
        if (newValue != null) {
            onRename(data.draft.id, newValue);
        }
        return false;
    },
    cellRenderer: TitleCell,
    cellEditor: TitleEditor,
    cellEditorPopup: true
});

// Every fixed width below fits its uppercase header and sort icon at the grid
// theme's header font, with about 10% to spare for wider system fonts, and
// fits the column's typical content.
const COST_ASSUMPTIONS_COLUMN: ColDef<BulkScenarioRow> = {
    headerName: "Cost assumptions",
    width: 210,
    valueGetter: ({ data }) =>
        data === undefined ? undefined : getCostAssumptionText(data.draft),
    cellRenderer: TruncatedTextCell
};

const sensitivitiesColumn = (
    onOpen: (id: string) => void
): ColDef<BulkScenarioRow, SensitivitiesValue> => ({
    headerName: "Sensitivities",
    width: 220,
    valueGetter: ({ data }) =>
        data === undefined
            ? undefined
            : {
                  scenarioTitle: data.draft.title,
                  sizeMetadata: data.sizeMetadata,
                  exceedsMwThreshold: data.exceedsMwThreshold
              },
    equals: isEqual,
    comparator: (a, b) =>
        (a?.sizeMetadata.percentageMwRemoved ?? 0) -
        (b?.sizeMetadata.percentageMwRemoved ?? 0),
    cellRenderer: ({
        data,
        value
    }: ICellRendererParams<BulkScenarioRow, SensitivitiesValue>) =>
        data === undefined || value == null ? null : (
            <SensitivitiesCell
                value={value}
                onOpen={() => onOpen(data.draft.id)}
            />
        )
});

interface CustomUpgradeValue {
    readonly upgradeName: string | undefined;
    readonly scenarioTitle: string;
}

const customUpgradeColumn = (
    onEdit: (id: string) => void
): ColDef<BulkScenarioRow, CustomUpgradeValue> => ({
    headerName: "Custom upgrade",
    flex: 1,
    minWidth: 240,
    valueGetter: ({ data }) =>
        data === undefined
            ? undefined
            : {
                  upgradeName: data.customUpgradeName,
                  scenarioTitle: data.draft.title
              },
    equals: isEqual,
    comparator: (a, b) =>
        (a?.upgradeName ?? "").localeCompare(b?.upgradeName ?? ""),
    cellRenderer: ({
        data,
        value
    }: ICellRendererParams<BulkScenarioRow, CustomUpgradeValue>) =>
        data === undefined || value == null ? null : (
            <TextLinkButton
                text={value.upgradeName ?? BulkScenarioUploadLabel.AddUpgrade}
                title={value.upgradeName}
                ariaLabel={`Edit custom upgrade for ${value.scenarioTitle}`}
                onClick={() => onEdit(data.draft.id)}
            />
        )
});

// The grid re-renders a cell only when its column value changes, so each
// value covers everything its cell shows, button labels included.
/** Columns for the bulk upload table; the callbacks must keep a stable identity. */
export const bulkScenarioTableColumns = ({
    showCostAssumptions,
    showCustomUpgrade,
    onRename,
    onOpenSensitivities,
    onEditCustomUpgrade,
    onShowWarnings,
    onRemove
}: {
    readonly showCostAssumptions: boolean;
    readonly showCustomUpgrade: boolean;
    readonly onRename: (id: string, title: string) => void;
    readonly onOpenSensitivities: (id: string) => void;
    readonly onEditCustomUpgrade: (id: string) => void;
    readonly onShowWarnings: (id: string) => void;
    readonly onRemove: (id: string) => void;
}): ColDef<BulkScenarioRow>[] => [
    {
        colId: "select",
        headerName: "",
        width: 44,
        pinned: "left",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        sortable: false,
        resizable: false,
        suppressMovable: true
    },
    titleColumn(onRename),
    sensitivitiesColumn(onOpenSensitivities),
    ...(showCostAssumptions ? [COST_ASSUMPTIONS_COLUMN] : []),
    ...(showCustomUpgrade ? [customUpgradeColumn(onEditCustomUpgrade)] : []),
    {
        headerName: "Run now",
        width: 110,
        valueGetter: ({ data }) =>
            data === undefined
                ? undefined
                : data.draft.runOnSubmit
                ? "Yes"
                : "Draft"
    },
    {
        headerName: "Warnings",
        width: 120,
        valueGetter: ({ data }) => data?.warningCount,
        cellRenderer: ({ data }: ICellRendererParams<BulkScenarioRow>) =>
            data === undefined ? null : (
                <WarningsCell
                    count={data.warningCount}
                    onShow={() => onShowWarnings(data.draft.id)}
                />
            )
    },
    {
        colId: "remove",
        headerName: "",
        width: 52,
        pinned: "right",
        sortable: false,
        resizable: false,
        suppressMovable: true,
        valueGetter: ({ data }) => data?.draft.title,
        cellRenderer: ({
            data,
            value
        }: ICellRendererParams<BulkScenarioRow, string>) =>
            data === undefined || value == null ? null : (
                <Button
                    variant="minimal"
                    icon={IconNames.TRASH}
                    aria-label={`Remove ${value}`}
                    onClick={() => onRemove(data.draft.id)}
                />
            )
    }
];
